import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ImagePlus, LogOut, Save, Trash2 } from "lucide-react";
import { BrandMark, BrandName } from "@/components/BrandMark";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { FALLBACK_SETTINGS } from "@/context/SettingsContext";
import { SelectField } from "@/components/SelectField";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const [form, setForm] = useState(FALLBACK_SETTINGS);
  const dirtyRef = useRef(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [flyers, setFlyers] = useState([]);
  const [flyerFile, setFlyerFile] = useState(null);
  const [flyerCaption, setFlyerCaption] = useState("");
  const [flyerBusy, setFlyerBusy] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ name: "", trip: "", quote: "", rating: 5 });
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [pdfTemplates, setPdfTemplates] = useState([]);
  const [testEmailBusy, setTestEmailBusy] = useState(false);
  const [dataBusy, setDataBusy] = useState(false);
  const [photosJob, setPhotosJob] = useState(null);
  const [restoreBusy, setRestoreBusy] = useState(false);

  const downloadDataBackup = async () => {
    setDataBusy(true);
    try {
      const res = await api.get("/admin/backup/data", {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `travelrush-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Full data backup downloaded — keep it somewhere safe.");
    } catch {
      toast.error("Could not create the data backup.");
    } finally {
      setDataBusy(false);
    }
  };

  const startPhotosBackup = async () => {
    try {
      await api.post("/admin/backup/photos", {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Photos backup started — this takes a minute or two.");
      const poll = setInterval(async () => {
        try {
          const st = await api.get("/admin/backup/photos/status", { headers: { Authorization: `Bearer ${token}` } });
          setPhotosJob(st.data);
          if (st.data.state !== "running") clearInterval(poll);
        } catch { clearInterval(poll); }
      }, 3000);
    } catch {
      toast.error("Could not start the photos backup.");
    }
  };

  const downloadPhotosPart = async (fname) => {
    try {
      const res = await api.get(`/admin/backup/photos/download/${fname}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download that backup file.");
    }
  };

  const restoreFile = async (file, kind) => {
    if (!file) return;
    setRestoreBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/admin/restore/${kind}`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      toast.success(res.data.message || "Restore complete.");
      if (kind === "data") window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed.");
    } finally {
      setRestoreBusy(false);
    }
  };

  const sendTestEmail = async () => {
    setTestEmailBusy(true);
    try {
      const res = await api.post("/admin/enquiries/test-email", {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || "Test alert sent.");
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("arihant_admin_token");
        localStorage.removeItem("arihant_admin_role");
        navigate("/admin/login");
      } else if (err.response?.status === 403) {
        toast.error("Admin role required for this action.");
      } else {
        toast.error(err.response?.data?.detail || "Could not send test alert.");
      }
    } finally {
      setTestEmailBusy(false);
    }
  };
  const navigate = useNavigate();
  const token = localStorage.getItem("arihant_admin_token");
  const role = localStorage.getItem("arihant_admin_role") || "admin";
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    if (role !== "admin") {
      toast.error("Admin role required for site settings.");
      navigate("/admin");
      return;
    }
    api
      .get("/settings")
      .then((res) => {
        // never clobber in-progress edits with a refetch
        if (dirtyRef.current) return;
        const { logo_url, ...rest } = res.data;
        setForm({ ...FALLBACK_SETTINGS, ...rest });
        setLogoUrl(logo_url ? `${BACKEND_URL}${logo_url}` : null);
      })
      .catch(() => {});
    loadFlyers();
    loadReviews();
    api.get("/pdf-templates").then((res) => setPdfTemplates(res.data || [])).catch(() => {});
  }, [token, navigate]);

  const loadFlyers = () => {
    api
      .get("/flyers")
      .then((res) => setFlyers(res.data || []))
      .catch(() => setFlyers([]));
  };

  const uploadFlyer = async () => {
    if (!flyerFile) return;
    setFlyerBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", flyerFile);
      fd.append("caption", flyerCaption);
      await api.post("/admin/flyers", fd, { headers });
      toast.success("Flyer uploaded — live in the homepage slideshow.");
      setFlyerFile(null);
      setFlyerCaption("");
      loadFlyers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Flyer upload failed.");
    } finally {
      setFlyerBusy(false);
    }
  };

  const deleteFlyer = async (id) => {
    setFlyerBusy(true);
    try {
      await api.delete(`/admin/flyers/${id}`, { headers });
      toast.success("Flyer removed.");
      loadFlyers();
    } catch {
      toast.error("Could not delete flyer.");
    } finally {
      setFlyerBusy(false);
    }
  };

  const loadReviews = () => {
    api
      .get("/testimonials")
      .then((res) => setReviews(res.data || []))
      .catch(() => setReviews([]));
  };

  const saveReview = async () => {
    if (!reviewForm.name.trim() || reviewForm.quote.trim().length < 10) {
      toast.error("Name and a quote of at least 10 characters are required.");
      return;
    }
    setReviewBusy(true);
    try {
      if (editingReviewId) {
        await api.put(`/admin/testimonials/${editingReviewId}`, reviewForm, { headers });
        toast.success("Review updated.");
      } else {
        await api.post("/admin/testimonials", reviewForm, { headers });
        toast.success("Review added — live on the homepage.");
      }
      setReviewForm({ name: "", trip: "", quote: "", rating: 5 });
      setEditingReviewId(null);
      loadReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save review.");
    } finally {
      setReviewBusy(false);
    }
  };

  const editReview = (r) => {
    setEditingReviewId(r.id);
    setReviewForm({ name: r.name, trip: r.trip || "", quote: r.quote, rating: r.rating || 5 });
  };

  const deleteReview = async (id) => {
    setReviewBusy(true);
    try {
      await api.delete(`/admin/testimonials/${id}`, { headers });
      toast.success("Review deleted.");
      if (editingReviewId === id) {
        setEditingReviewId(null);
        setReviewForm({ name: "", trip: "", quote: "", rating: 5 });
      }
      loadReviews();
    } catch {
      toast.error("Could not delete review.");
    } finally {
      setReviewBusy(false);
    }
  };

  const update = (key) => (e) => {
    dirtyRef.current = true;
    setForm({ ...form, [key]: e.target.value });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        company_name: form.company_name,
        tagline: form.tagline,
        phone: form.phone,
        email: form.email,
        address: form.address,
        whatsapp: form.whatsapp,
        stat_years: Number(form.stat_years) || 0,
        stat_countries: Number(form.stat_countries) || 0,
        stat_travellers: Number(form.stat_travellers) || 0,
        stat_experts: Number(form.stat_experts) || 0,
        stat_visa_success: form.stat_visa_success,
        color_bg: form.color_bg,
        color_surface: form.color_surface,
        color_card: form.color_card,
        color_accent: form.color_accent,
        color_text: form.color_text,
        pdf_template: form.pdf_template || "royal",
        dmc_hub_url: form.dmc_hub_url || "https://dmchub.emergent.host",
        why_1_title: form.why_1_title, why_1_desc: form.why_1_desc,
        why_2_title: form.why_2_title, why_2_desc: form.why_2_desc,
        why_3_title: form.why_3_title, why_3_desc: form.why_3_desc,
        why_4_title: form.why_4_title, why_4_desc: form.why_4_desc,
      }, { headers });
      toast.success("Company details updated — live across the site.");
      dirtyRef.current = false;
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("arihant_admin_token");
        localStorage.removeItem("arihant_admin_role");
        navigate("/admin/login");
      } else if (err.response?.status === 403) {
        toast.error("Admin role required for this action.");
      } else {
        toast.error("Could not save settings.");
      }
    } finally {
      setSaving(false);
    }
  };

  const pickLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", logoFile);
      await api.post("/admin/settings/logo", data, { headers });
      toast.success("Logo updated — refreshing to see it live.");
      setTimeout(() => window.location.reload(), 900);
    } catch {
      toast.error("Logo upload failed. Use an image under 3 MB.");
    } finally {
      setUploading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("arihant_admin_token");
    localStorage.removeItem("arihant_admin_role");
    navigate("/admin/login");
  };

  return (
    <div className="bg-ink min-h-screen text-cream" data-testid="admin-settings-page">
      <header className="border-b border-gold/15 bg-surface/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-settings-logo">
            <BrandMark size={36} iconSize={16} />
            <span className="font-display text-xl text-cream">
              <BrandName />
              <span className="text-xs font-mono tracking-widest text-faded ml-3 uppercase">{role}</span>
            </span>
          </Link>
          <nav className="flex items-center gap-3 flex-wrap">
            <Link to="/admin" data-testid="admin-tab-enquiries" className="text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">
              Enquiries
            </Link>
            <Link to="/admin/packages" data-testid="admin-tab-packages" className="text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">
              Packages
            </Link>
            {role === "admin" && (
              <Link to="/admin/users" data-testid="admin-tab-users" className="text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">
                Users
              </Link>
            )}
            <span data-testid="admin-tab-settings" className="text-xs text-ink bg-gold px-5 py-2.5 rounded-full font-semibold">
              Site Settings
            </span>
            <button
              onClick={logout}
              data-testid="admin-settings-logout-btn"
              className="flex items-center gap-2 text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
        <div className="mb-10">
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-3">Site Settings</p>
          <h1 className="font-display text-3xl sm:text-4xl text-cream">Company details</h1>
          <p className="text-sm text-faded mt-2">Changes go live across the whole website immediately.</p>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-8">
          <div className="rounded-3xl border border-gold/20 bg-card/70 p-6 sm:p-10">
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-6">Brand Logo</p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="w-28 h-28 rounded-2xl border border-dashed border-gold/40 bg-ink/60 flex items-center justify-center overflow-hidden" data-testid="logo-preview">
                {logoPreview || logoUrl ? (
                  <img src={logoPreview || logoUrl} alt="Company logo" className="max-w-full max-h-full object-contain" />
                ) : (
                  <ImagePlus size={30} className="text-gold/60" strokeWidth={1.25} />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-mist mb-4">Upload a PNG, JPG, WebP or SVG logo from your computer (max 3 MB).</p>
                <div className="flex flex-wrap items-center gap-3">
                  <label
                    data-testid="logo-file-label"
                    className="cursor-pointer flex items-center gap-2 text-xs font-semibold text-gold border border-gold/40 px-5 py-3 rounded-full hover:bg-gold/10 transition-colors"
                  >
                    <ImagePlus size={15} /> Choose from device
                    <input type="file" accept="image/*" onChange={pickLogo} className="hidden" data-testid="logo-file-input" />
                  </label>
                  {logoFile && (
                    <button
                      onClick={uploadLogo}
                      disabled={uploading}
                      data-testid="logo-upload-btn"
                      className="text-xs font-semibold text-ink bg-gold px-5 py-3 rounded-full hover:bg-gold-hover transition-colors disabled:opacity-60"
                    >
                      {uploading ? "Uploading…" : "Upload Logo"}
                    </button>
                  )}
                </div>
                {logoFile && <p className="text-xs text-faded mt-3">Selected: {logoFile.name}</p>}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-gold/20 bg-card/70 p-6 sm:p-10" data-testid="flyer-manager">
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-2">Homepage Flyer Slideshow</p>
            <p className="text-xs text-faded mb-6">
              Upload square flyers (1080 × 1080 px, JPG/PNG, max 8 MB) for upcoming tours & promotions — they appear in the
              auto-playing slideshow above "01 — The Collections" on the homepage.
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <label
                data-testid="flyer-file-label"
                className="cursor-pointer flex items-center gap-2 text-xs font-semibold text-gold border border-gold/40 px-5 py-3 rounded-full hover:bg-gold/10 transition-colors"
              >
                <ImagePlus size={15} /> Choose flyer from device
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="flyer-file-input"
                  onChange={(e) => setFlyerFile(e.target.files?.[0] || null)}
                />
              </label>
              <input
                value={flyerCaption}
                onChange={(e) => setFlyerCaption(e.target.value)}
                data-testid="flyer-caption-input"
                className="field-input flex-1 min-w-[200px]"
                placeholder="Caption (optional) — e.g. Europe Group Departure, Oct 2026"
                maxLength={120}
              />
              {flyerFile && (
                <button
                  onClick={uploadFlyer}
                  disabled={flyerBusy}
                  data-testid="flyer-upload-btn"
                  className="text-xs font-semibold text-ink bg-gold px-5 py-3 rounded-full hover:bg-gold-hover transition-colors disabled:opacity-60"
                >
                  {flyerBusy ? "Uploading…" : "Upload Flyer"}
                </button>
              )}
            </div>
            {flyerFile && <p className="text-xs text-faded mb-6">Selected: {flyerFile.name}</p>}

            {flyers.length === 0 ? (
              <p className="text-sm text-faded" data-testid="flyers-empty">No flyers yet — the slideshow stays hidden until you upload one.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="flyer-list">
                {flyers.map((f) => (
                  <div key={f.id} className="relative group rounded-2xl overflow-hidden border border-gold/15 bg-ink/60" data-testid={`flyer-item-${f.id}`}>
                    <img
                      src={`${BACKEND_URL}${f.image_url}`}
                      alt={f.caption || "Flyer"}
                      className="w-full aspect-square object-cover"
                    />
                    {f.caption && (
                      <p className="text-[11px] text-mist px-3 py-2 truncate">{f.caption}</p>
                    )}
                    <button
                      onClick={() => deleteFlyer(f.id)}
                      disabled={flyerBusy}
                      data-testid={`flyer-delete-${f.id}`}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-ink/80 border border-red-400/40 text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 hover:text-white"
                      title="Delete flyer"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-gold/20 bg-card/70 p-6 sm:p-10" data-testid="review-manager">
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-2">Homepage Reviews</p>
            <p className="text-xs text-faded mb-6">Add, edit or remove the traveller reviews shown in the "Happy Travellers" carousel on the homepage.</p>

            <div className="rounded-2xl border border-cream/10 bg-ink/40 p-5 mb-8 space-y-4">
              <p className="text-xs font-mono tracking-widest uppercase text-mist">
                {editingReviewId ? "Editing review" : "Add a new review"}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <input
                  value={reviewForm.name}
                  onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                  data-testid="review-name-input"
                  className="field-input"
                  placeholder="Traveller name"
                />
                <input
                  value={reviewForm.trip}
                  onChange={(e) => setReviewForm({ ...reviewForm, trip: e.target.value })}
                  data-testid="review-trip-input"
                  className="field-input"
                  placeholder="Trip — e.g. Dubai · Family Tour"
                />
                <SelectField
                  value={String(reviewForm.rating)}
                  onChange={(v) => setReviewForm({ ...reviewForm, rating: Number(v) })}
                  testid="review-rating-select"
                  options={[5, 4, 3, 2, 1].map((r) => ({ value: String(r), label: `${r} Star${r > 1 ? "s" : ""}` }))}
                />
              </div>
              <textarea
                rows={3}
                value={reviewForm.quote}
                onChange={(e) => setReviewForm({ ...reviewForm, quote: e.target.value })}
                data-testid="review-quote-input"
                className="field-input resize-y"
                placeholder="Review text (10-600 characters)"
                maxLength={600}
              />
              <div className="flex gap-3">
                <button
                  onClick={saveReview}
                  disabled={reviewBusy}
                  data-testid="review-save-btn"
                  className="text-xs font-semibold text-ink bg-gold px-6 py-3 rounded-full hover:bg-gold-hover transition-colors disabled:opacity-60"
                >
                  {reviewBusy ? "Saving…" : editingReviewId ? "Update Review" : "Add Review"}
                </button>
                {editingReviewId && (
                  <button
                    onClick={() => { setEditingReviewId(null); setReviewForm({ name: "", trip: "", quote: "", rating: 5 }); }}
                    data-testid="review-cancel-edit-btn"
                    className="text-xs text-mist border border-cream/20 px-6 py-3 rounded-full hover:border-gold hover:text-gold transition-colors"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-3" data-testid="review-list">
              {reviews.map((r) => (
                <div key={r.id} className="flex items-start gap-4 rounded-2xl border border-cream/10 bg-ink/40 p-4" data-testid={`review-item-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-cream">
                      {r.name}
                      <span className="text-gold text-xs ml-2">{"★".repeat(r.rating || 5)}</span>
                    </p>
                    {r.trip && <p className="text-[11px] font-mono tracking-widest uppercase text-faded mt-0.5">{r.trip}</p>}
                    <p className="text-xs text-mist mt-2 line-clamp-2">"{r.quote}"</p>
                  </div>
                  <button
                    onClick={() => editReview(r)}
                    data-testid={`review-edit-${r.id}`}
                    className="text-xs text-gold border border-gold/30 px-4 py-2 rounded-full hover:bg-gold/10 transition-colors shrink-0"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteReview(r.id)}
                    disabled={reviewBusy}
                    data-testid={`review-delete-${r.id}`}
                    className="text-xs text-red-400 border border-red-400/30 px-4 py-2 rounded-full hover:bg-red-500/10 transition-colors shrink-0"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={save} className="rounded-3xl border border-gold/20 bg-card/70 p-6 sm:p-10 space-y-5" data-testid="settings-form">
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-2">Company Information</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Company Name *</label>
                <input required value={form.company_name} onChange={update("company_name")} data-testid="settings-name-input" className="field-input" />
              </div>
              <div>
                <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Tagline</label>
                <input value={form.tagline} onChange={update("tagline")} data-testid="settings-tagline-input" className="field-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Mobile No *</label>
                <input required value={form.phone} onChange={update("phone")} data-testid="settings-phone-input" className="field-input" />
              </div>
              <div>
                <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Email *</label>
                <input required type="email" value={form.email} onChange={update("email")} data-testid="settings-email-input" className="field-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">WhatsApp No</label>
                <input value={form.whatsapp} onChange={update("whatsapp")} data-testid="settings-whatsapp-input" className="field-input" />
              </div>
              <div>
                <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Address</label>
                <input value={form.address} onChange={update("address")} data-testid="settings-address-input" className="field-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">DMC Hub Portal URL</label>
              <input value={form.dmc_hub_url || ""} onChange={update("dmc_hub_url")} data-testid="settings-dmc-hub-input" className="field-input" placeholder="https://dmchub.emergent.host" />
              <p className="text-[11px] text-faded mt-2">Linked from the footer "DMC Hub" link and embedded at /dmc-hub. For internal team use.</p>
            </div>
            <div className="border-t border-gold/15 pt-7" data-testid="settings-email-alerts-section">
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-2">Enquiry Email Alerts</p>
              <p className="text-xs text-faded mb-4">
                Every new enquiry is emailed instantly to the Email address above ({form.email || "…"}). Save after changing it.
              </p>
              <button
                type="button"
                onClick={sendTestEmail}
                disabled={testEmailBusy}
                data-testid="send-test-email-btn"
                className="text-xs font-mono tracking-widest uppercase px-5 py-2.5 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors disabled:opacity-50"
              >
                {testEmailBusy ? "Sending…" : "Send Test Alert"}
              </button>
            </div>
            <div className="border-t border-gold/15 pt-7">
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-5">Website Stats (shown on homepage)</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-5">
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Years</label>
                  <input type="number" min="0" value={form.stat_years} onChange={update("stat_years")} data-testid="settings-stat-years" className="field-input" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Countries</label>
                  <input type="number" min="0" value={form.stat_countries} onChange={update("stat_countries")} data-testid="settings-stat-countries" className="field-input" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Happy Travellers</label>
                  <input type="number" min="0" value={form.stat_travellers} onChange={update("stat_travellers")} data-testid="settings-stat-travellers" className="field-input" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Experts</label>
                  <input type="number" min="0" value={form.stat_experts} onChange={update("stat_experts")} data-testid="settings-stat-experts" className="field-input" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Visa Success</label>
                  <input value={form.stat_visa_success} onChange={update("stat_visa_success")} data-testid="settings-stat-visa" className="field-input" placeholder="99.4%" />
                </div>
              </div>
            </div>

            <div className="border-t border-gold/15 pt-7">
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-2">Website Colours</p>
              <p className="text-xs text-faded mb-5">Applied live across the whole website on save (refresh any page to see).</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                {[
                  ["color_bg", "Main Background", "settings-color-bg"],
                  ["color_surface", "Section Surface", "settings-color-surface"],
                  ["color_card", "Card Background", "settings-color-card"],
                  ["color_accent", "Accent (Gold)", "settings-color-accent"],
                  ["color_text", "Font Colour", "settings-color-text"],
                ].map(([key, label, testid]) => (
                  <div key={key}>
                    <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">{label}</label>
                    <div className="flex items-center gap-3">
                      <input type="color" value={form[key] || "#0A1C30"} onChange={update(key)} data-testid={testid} className="w-12 h-11 rounded-lg border border-gold/30 bg-transparent cursor-pointer p-1" />
                      <input value={form[key] || ""} onChange={update(key)} className="field-input flex-1 font-mono text-xs" placeholder="#0A1C30" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-gold/15 pt-7" data-testid="settings-pdf-template-section">
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-2">PDF Download Template</p>
              <p className="text-xs text-faded mb-5">Choose the design travellers get when they download a package or visa PDF.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {pdfTemplates.map((t) => {
                  const active = (form.pdf_template || "royal") === t.id;
                  return (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => { dirtyRef.current = true; setForm({ ...form, pdf_template: t.id }); }}
                      data-testid={`pdf-template-${t.id}`}
                      className={`text-left rounded-2xl border p-4 transition-all ${
                        active
                          ? "border-gold bg-gold/10 shadow-[0_0_0_1px_rgba(245,163,0,0.4)]"
                          : "border-cream/15 bg-card/40 hover:border-gold/40"
                      }`}
                    >
                      <div className="mb-3 rounded-xl overflow-hidden border border-cream/15 bg-ink/80 aspect-[210/260] flex items-start justify-center">
                        <img
                          src={`${API}/pdf-templates/${t.id}/preview`}
                          alt={`${t.name} PDF preview`}
                          loading="lazy"
                          data-testid={`pdf-template-preview-${t.id}`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm text-cream">{t.name}</span>
                        <span className="flex gap-1.5">
                          {(t.swatches || []).map((s) => (
                            <span key={s} className="w-3.5 h-3.5 rounded-full border border-cream/25" style={{ backgroundColor: s }} />
                          ))}
                        </span>
                      </div>
                      <p className="text-xs text-faded leading-relaxed">{t.description}</p>
                      {active && <p className="mt-2 text-[10px] font-mono tracking-widest uppercase text-gold">Selected</p>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-gold/15 pt-7" data-testid="settings-backup-section">
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-2">Backup & Restore</p>
              <p className="text-xs text-faded mb-5">
                Download everything — all packages, visas, users, settings, reviews and enquiries — plus every photo.
                If the app ever crashes or loses data, upload these files back here to restore the site fully.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="rounded-xl border border-cream/15 p-5 space-y-3">
                  <p className="text-sm font-semibold text-cream">Download (backup)</p>
                  <button
                    type="button"
                    onClick={downloadDataBackup}
                    disabled={dataBusy}
                    data-testid="backup-data-btn"
                    className="w-full text-xs font-mono tracking-widest uppercase px-4 py-3 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors disabled:opacity-50"
                  >
                    {dataBusy ? "Preparing…" : "Download All Data (JSON)"}
                  </button>
                  {photosJob?.state === "done" ? (
                    photosJob.files.map((f) => (
                      <button
                        type="button"
                        key={f}
                        onClick={() => downloadPhotosPart(f)}
                        data-testid={`backup-photos-download-${f}`}
                        className="w-full text-xs font-mono tracking-widest uppercase px-4 py-3 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors"
                      >
                        Download Photos: {f.split("-").pop()}
                      </button>
                    ))
                  ) : (
                    <button
                      type="button"
                      onClick={startPhotosBackup}
                      disabled={photosJob?.state === "running"}
                      data-testid="backup-photos-btn"
                      className="w-full text-xs font-mono tracking-widest uppercase px-4 py-3 rounded-full border border-cream/25 text-mist hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
                    >
                      {photosJob?.state === "running"
                        ? `Backing up photos… ${photosJob.done}/${photosJob.total}`
                        : photosJob?.state === "error"
                          ? "Photos backup failed — retry"
                          : "Download All Photos (ZIP)"}
                    </button>
                  )}
                  {photosJob?.state === "error" && (
                    <p className="text-[11px] text-red-400">{photosJob.error}</p>
                  )}
                </div>
                <div className="rounded-xl border border-cream/15 p-5 space-y-3">
                  <p className="text-sm font-semibold text-cream">Restore</p>
                  <label className="block">
                    <span className="block text-xs font-mono tracking-widest uppercase text-mist border border-dashed border-cream/25 rounded-full px-4 py-3 text-center cursor-pointer hover:border-gold hover:text-gold transition-colors" data-testid="restore-data-label">
                      {restoreBusy ? "Restoring…" : "Upload Data Backup (.json)"}
                    </span>
                    <input
                      type="file"
                      accept=".json,application/json"
                      className="hidden"
                      data-testid="restore-data-input"
                      disabled={restoreBusy}
                      onChange={(e) => { restoreFile(e.target.files?.[0], "data"); e.target.value = ""; }}
                    />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-mono tracking-widest uppercase text-mist border border-dashed border-cream/25 rounded-full px-4 py-3 text-center cursor-pointer hover:border-gold hover:text-gold transition-colors" data-testid="restore-photos-label">
                      {restoreBusy ? "Restoring…" : "Upload Photos Backup (.zip)"}
                    </span>
                    <input
                      type="file"
                      accept=".zip,application/zip"
                      className="hidden"
                      data-testid="restore-photos-input"
                      disabled={restoreBusy}
                      onChange={(e) => { restoreFile(e.target.files?.[0], "photos"); e.target.value = ""; }}
                    />
                  </label>
                  <p className="text-[11px] text-faded">Restore merges by package ID — existing items are updated, missing items recreated. Upload every photos part if there are several.</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gold/20 bg-card/50 p-7" data-testid="settings-whyus-section">
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-2">Why Us Section (Homepage)</p>
              <p className="text-xs text-faded mb-5">The four "Why Us" points shown on the homepage — edit titles and descriptions here.</p>
              <div className="space-y-6">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-6 border-b border-cream/10 last:border-0 last:pb-0">
                    <div>
                      <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Point {n} — Title</label>
                      <input
                        value={form[`why_${n}_title`] || ""}
                        onChange={update(`why_${n}_title`)}
                        data-testid={`settings-why-${n}-title`}
                        className="field-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Point {n} — Description</label>
                      <textarea
                        rows={3}
                        value={form[`why_${n}_desc`] || ""}
                        onChange={update(`why_${n}_desc`)}
                        data-testid={`settings-why-${n}-desc`}
                        className="field-input resize-y"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              data-testid="settings-save-btn"
              className="flex items-center gap-2 bg-gold text-ink font-semibold text-sm px-10 py-4 rounded-full hover:bg-gold-hover transition-colors disabled:opacity-60"
            >
              <Save size={15} /> {saving ? "Saving…" : "Save Changes"}
            </button>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
